import React from 'react';

import {
  FileOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import ListPageHeader from '@shared/ListPageHeader';
import {
  Button,
  Card,
  Typography,
  Descriptions,
  Tag,
  List,
  Table
} from 'antd';
import { useTranslation } from 'react-i18next';

import { formatDateTime } from '../../../utils/dateTime';

const { Text } = Typography;

export default function ImportShow({
  importData,
  role = 'admin',
  merchants = []
}) {
  const { t } = useTranslation();

  const getMerchantName = (merchantName) => {
    return merchantName || '-';
  };

  // Helper function to translate error messages.
  // Tries `primaryKey` first; if missing, falls back to `fallbackKey` (e.g. shared Rails-symbol
  // dictionary `import.shared.model_errors.<sym>`); if both missing, surfaces `primaryKey` raw so
  // the gap is debuggable instead of silently blank.
  const translateErrorMessage = (primaryKey, keys = [], fallbackKey = null) => {
    try {
      const primaryTemplate = t(primaryKey, { defaultValue: '' });
      const fallbackTemplate = fallbackKey ? t(fallbackKey, { defaultValue: '' }) : '';
      const templateForIntrospection = primaryTemplate || fallbackTemplate || primaryKey;
      const needsIndexedKeys = /keys\[\d+\]/.test(templateForIntrospection);

      // Convert keys to interpolation parameters
      let interpolationParams = {};
      if (Array.isArray(keys) && keys.length > 0) {
        if (typeof keys[0] === 'object' && keys[0] !== null) {
          keys.forEach(keyObj => {
            if (typeof keyObj === 'object' && keyObj !== null) {
              interpolationParams = { ...interpolationParams, ...keyObj };
            }
          });
        } else if (needsIndexedKeys) {
          keys.forEach((value, index) => {
            interpolationParams[`keys[${index}]`] = value;
          });
        } else {
          interpolationParams['keys'] = keys.join(',');
        }
      } else if (typeof keys === 'object' && keys !== null && !Array.isArray(keys)) {
        interpolationParams = keys;
      }

      // If template uses {{keys}} but no keys provided, set empty string
      if (!needsIndexedKeys && templateForIntrospection.includes('{{keys}}') && !interpolationParams.hasOwnProperty('keys')) {
        interpolationParams['keys'] = '';
      }

      // i18next defaultValue chain: primary missing -> fallback (translated) -> primaryKey raw.
      const defaultValue = fallbackKey
        ? t(fallbackKey, { defaultValue: primaryKey, ...interpolationParams })
        : primaryKey;

      return t(primaryKey, {
        defaultValue,
        ...interpolationParams
      });
    } catch (error) {
      console.error('Error translating message:', error);
      return primaryKey;
    }
  };

  // Process error messages for display
  const processErrorMessages = () => {
    if (!importData.error_messages || typeof importData.error_messages !== 'object') {
      return null;
    }

    const errorEntries = Object.entries(importData.error_messages);
    if (errorEntries.length === 0) {
      return null;
    }

    // Sort by row index (convert to number for proper sorting)
    const sortedEntries = errorEntries.sort((a, b) => {
      const rowA = parseInt(a[0], 10) || 0;
      const rowB = parseInt(b[0], 10) || 0;
      return rowA - rowB;
    });

    return sortedEntries.map(([rowIndex, errors]) => {
      const errorList = Array.isArray(errors) ? errors : [];
      const translatedErrors = errorList.map((error) => {
        // Handle both formats:
        // 1. Object format: { error_message: "key", keys: [] }
        // 2. Simple string format: "key"
        if (typeof error === 'object' && error !== null) {
          const errorKey = error.error_message || error.message || error;
          const errorKeys = error.keys || [];
          const fallbackKey = error.fallback_message || null;
          return translateErrorMessage(errorKey, errorKeys, fallbackKey);
        } else {
          // Simple string format
          return translateErrorMessage(error, []);
        }
      });

      return {
        rowIndex,
        errors: translatedErrors,
        errorCount: translatedErrors.length
      };
    });
  };

  const errorMessagesData = processErrorMessages();

  if (!importData) {
    return (
      <div>
        <Card>
          <p>{t('shared.imports.detail.not_found')}</p>
        </Card>
      </div>
    );
  }

  const headerBasePath = role === 'operator' ? '/operator' : '/admin';

  return (
    <div>
      <ListPageHeader
        basePath={headerBasePath}
        items={['data', { label: t(`${role === 'operator' ? 'operator' : 'admin'}.menu.data.imports`, '匯入紀錄'), path: '/imports' }, { label: importData.name || `#${importData.id}` }]}
      />
      <Card
        title={t('shared.imports.detail.title')}
        style={{ marginBottom: 16 }}
      >
        <Descriptions column={1} bordered>
          <Descriptions.Item label={t('shared.imports.list.columns.id')}>
            {importData.name || importData.id}
          </Descriptions.Item>
          <Descriptions.Item label={t('shared.imports.list.columns.import_type')}>
            <Tag color="blue">
              {t(`common.import_type.${importData.import_type}`)}
            </Tag>
          </Descriptions.Item>
          {importData.status && (
            <Descriptions.Item label={t('shared.imports.list.columns.status')}>
              {(() => {
                const statusConfig = {
                  pending: { color: 'default', label: t('common.import_status.pending') },
                  parsing: { color: 'processing', label: t('common.import_status.parsing') },
                  importing: { color: 'processing', label: t('common.import_status.importing') },
                  partially_completed: { color: 'warning', label: t('common.import_status.partially_completed') },
                  completed: { color: 'success', label: t('common.import_status.completed') },
                  failed: { color: 'error', label: t('common.import_status.failed') }
                }
                const config = statusConfig[importData.status] || { color: 'default', label: importData.status }
                return <Tag color={config.color}>{config.label}</Tag>
              })()}
            </Descriptions.Item>
          )}
          {role === 'operator' && importData.merchant && (
            <Descriptions.Item label={t('shared.imports.list.columns.merchant')}>
              {importData.merchant}
            </Descriptions.Item>
          )}
          {importData.sales_channel && (
            <Descriptions.Item label={t('shared.imports.detail.sales_channel')}>
              {importData.sales_channel}
            </Descriptions.Item>
          )}
          {importData.user && (
            <Descriptions.Item label={t('shared.imports.detail.user')}>
              {importData.user}
            </Descriptions.Item>
          )}
          {importData.operator && (
            <Descriptions.Item label={t('shared.imports.detail.operator')}>
              {importData.operator}
            </Descriptions.Item>
          )}
          {importData.import_file_url && (
            <Descriptions.Item label={t('shared.imports.detail.file')}>
              <a href={importData.import_file_url} target="_blank" rel="noopener noreferrer">
                <FileOutlined /> {t('shared.imports.detail.view_file')}
              </a>
            </Descriptions.Item>
          )}
          {importData.message && (
            <Descriptions.Item label={t('shared.imports.detail.message')}>
              {importData.message}
            </Descriptions.Item>
          )}
          {(importData.inserted_rows != null || importData.updated_rows != null) && (
            <>
              {importData.inserted_rows != null && (
                <Descriptions.Item label={t('shared.imports.list.columns.inserted_rows')}>
                  {importData.inserted_rows}
                </Descriptions.Item>
              )}
              {importData.updated_rows != null && (
                <Descriptions.Item label={t('shared.imports.list.columns.updated_rows')}>
                  {importData.updated_rows}
                </Descriptions.Item>
              )}
            </>
          )}
          <Descriptions.Item label={t('common.created_at')}>
            {formatDateTime(importData.created_at)}
          </Descriptions.Item>
          <Descriptions.Item label={t('common.updated_at')}>
            {formatDateTime(importData.updated_at)}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {errorMessagesData && errorMessagesData.length > 0 && (
        <Card
          title={t('shared.imports.detail.error_messages_title', {
            count: errorMessagesData.reduce((sum, item) => sum + item.errorCount, 0)
          })}
          style={{ marginBottom: 16 }}
        >
          <Table
            dataSource={errorMessagesData}
            rowKey="rowIndex"
            size="small"
            pagination={errorMessagesData.length > 20 ? { pageSize: 20, showSizeChanger: true, showTotal: (total) => t('common.table.total', { total }) } : false}
            columns={[
              {
                title: t('shared.imports.detail.row_column', '行'),
                dataIndex: 'rowIndex',
                key: 'rowIndex',
                width: 80,
                align: 'center',
              },
              {
                title: t('shared.imports.detail.error_column', '錯誤訊息'),
                dataIndex: 'errors',
                key: 'errors',
                render: (errors) => (
                  <div>
                    {errors.map((error, idx) => (
                      <div key={idx}>
                        <Text type="danger">{error}</Text>
                      </div>
                    ))}
                  </div>
                ),
              },
            ]}
          />
        </Card>
      )}

      {importData.attachments && importData.attachments.length > 0 && (
        <Card title={t('shared.imports.detail.attachments')}>
          <List
            dataSource={importData.attachments}
            renderItem={(attachment) => (
              <List.Item
                actions={[
                  <Button
                    key="download"
                    type="link"
                    icon={<DownloadOutlined />}
                    href={attachment.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('common.button.download')}
                  </Button>
                ]}
              >
                <List.Item.Meta
                  avatar={<FileOutlined style={{ fontSize: 24 }} />}
                  title={
                    <a href={attachment.file_url} target="_blank" rel="noopener noreferrer">
                      {attachment.filename || t('shared.imports.detail.attachment')}
                    </a>
                  }
                  description={
                    <Text type="secondary">
                      {t('shared.imports.detail.uploaded_at')}: {formatDateTime(attachment.created_at)}
                    </Text>
                  }
                />
              </List.Item>
            )}
          />
        </Card>
      )}
    </div>
  );
}
